import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, timer } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

export type MotivoActividadUsuario =
  | 'INICIO'
  | 'ACTIVIDAD'
  | 'VISIBILIDAD'
  | 'INACTIVIDAD'
  | 'PESTANA_OCULTA';

export interface EstadoActividadUsuario {
  activo: boolean;
  motivo: MotivoActividadUsuario;
}

@Injectable({ providedIn: 'root' })
export class UsuariosActividadTrackerService implements OnDestroy {
  private readonly estadoSubject = new BehaviorSubject<EstadoActividadUsuario>({
    activo: true,
    motivo: 'INICIO',
  });
  private temporizadorInactividad: any;
  private temporizadorPestanaOculta: any;
  private iniciado = false;
  private readonly tiempoInactividadMs = 15 * 60 * 1000;
  private readonly tiempoPestanaOcultaMs = 5 * 60 * 1000;

  readonly estado$ = this.estadoSubject.asObservable();
  readonly activo$ = this.estado$.pipe(
    map(estado => estado.activo),
    distinctUntilChanged()
  );

  /**
   * Conserva una referencia estable para registrar y retirar los eventos de
   * interacción del documento desde el único tracker global.
   */
  private readonly registrarActividadListener = (): void => {
    this.registrarActividad('ACTIVIDAD');
  };

  /**
   * Conserva una referencia estable para observar cambios de visibilidad y
   * aplicar la gracia especial de pestañas en segundo plano.
   */
  private readonly gestionarVisibilidadListener = (): void => {
    this.gestionarVisibilidad();
  };

  /**
   * Usa NgZone únicamente al publicar transiciones; la captura continua de
   * actividad permanece fuera de la detección de cambios de Angular.
   */
  constructor(private zone: NgZone) {}

  /**
   * Retira listeners y vencimientos si Angular destruye el injector, evitando
   * conservar detectores duplicados en pruebas o reconstrucciones de la app.
   */
  ngOnDestroy(): void {
    document.removeEventListener('pointerdown', this.registrarActividadListener);
    document.removeEventListener('keydown', this.registrarActividadListener);
    document.removeEventListener('wheel', this.registrarActividadListener);
    document.removeEventListener(
      'visibilitychange',
      this.gestionarVisibilidadListener
    );
    this.limpiarTemporizadores();
    this.iniciado = false;
  }

  /**
   * Instala una sola vez los listeners globales y programa el umbral que
   * gobierna WebSocket y pollings sin depender de un componente concreto.
   */
  iniciar(): void {
    if (this.iniciado) return;
    this.iniciado = true;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointerdown', this.registrarActividadListener);
      document.addEventListener('keydown', this.registrarActividadListener);
      document.addEventListener('wheel', this.registrarActividadListener, {
        passive: true,
      });
      document.addEventListener(
        'visibilitychange',
        this.gestionarVisibilidadListener
      );
      this.gestionarVisibilidad();
    });
  }

  /**
   * Publica el estado vigente para consumidores que deben decidir de forma
   * síncrona si pueden abrir una conexión o iniciar una consulta periódica.
   */
  get estaActivo(): boolean {
    return this.estadoSubject.value.activo;
  }

  /**
   * Crea un sondeo que existe únicamente mientras hay actividad: al vencer el
   * umbral cancela el timer actual, registra la causa y al regresar lo reinicia.
   */
  crearPolling(
    periodoMs: number,
    nombre = 'Polling'
  ): Observable<number> {
    this.iniciar();
    return this.estado$.pipe(
      distinctUntilChanged((anterior, actual) =>
        anterior.activo === actual.activo
      ),
      tap(estado => this.registrarEstadoPolling(nombre, estado)),
      switchMap(estado => estado.activo ? timer(0, periodoMs) : EMPTY)
    );
  }

  /**
   * Explica en consola el ciclo de vida de cada timer sin incluir token,
   * usuario ni datos de negocio pertenecientes al consumidor del polling.
   */
  private registrarEstadoPolling(
    nombre: string,
    estado: EstadoActividadUsuario
  ): void {
    if (!estado.activo) {
      console.info(
        estado.motivo === 'INACTIVIDAD'
          ? `[Polling: ${nombre}] Timer cancelado: 15 minutos sin actividad.`
          : `[Polling: ${nombre}] Timer cancelado: pestaña oculta durante 5 minutos.`
      );
      return;
    }

    if (estado.motivo === 'INICIO') {
      console.info(`[Polling: ${nombre}] Timer iniciado.`);
      return;
    }

    console.info(
      estado.motivo === 'VISIBILIDAD'
        ? `[Polling: ${nombre}] Timer reiniciado: la pestaña volvió a estar visible.`
        : `[Polling: ${nombre}] Timer reiniciado: se detectó actividad del usuario.`
    );
  }

  /**
   * Renueva el umbral general ante interacción visible y publica la
   * reanudación una sola vez para que cada consumidor reconstruya su estado.
   */
  registrarActividad(
    motivo: 'ACTIVIDAD' | 'VISIBILIDAD' = 'ACTIVIDAD'
  ): void {
    if (document.hidden) return;
    this.limpiarTemporizadorPestanaOculta();
    if (!this.estadoSubject.value.activo) {
      this.publicarEstado({ activo: true, motivo });
    }
    this.programarInactividad();
  }

  /**
   * Sustituye el umbral normal por cinco minutos de gracia al ocultar la
   * pestaña y reactiva inmediatamente el sistema cuando vuelve al frente.
   */
  private gestionarVisibilidad(): void {
    if (!document.hidden) {
      this.registrarActividad('VISIBILIDAD');
      return;
    }

    clearTimeout(this.temporizadorInactividad);
    this.temporizadorInactividad = null;
    if (!this.estadoSubject.value.activo) return;
    this.limpiarTemporizadorPestanaOculta();
    this.temporizadorPestanaOculta = setTimeout(
      () => this.marcarInactivo('PESTANA_OCULTA'),
      this.tiempoPestanaOcultaMs
    );
  }

  /**
   * Reemplaza el vencimiento visible anterior y marca inactividad después de
   * quince minutos completos sin teclado, rueda ni interacción de puntero.
   */
  private programarInactividad(): void {
    clearTimeout(this.temporizadorInactividad);
    this.temporizadorInactividad = setTimeout(
      () => this.marcarInactivo('INACTIVIDAD'),
      this.tiempoInactividadMs
    );
  }

  /**
   * Publica una única transición inactiva y elimina callbacks pendientes para
   * que ningún consumidor se reactive sin una interacción real posterior.
   */
  private marcarInactivo(
    motivo: 'INACTIVIDAD' | 'PESTANA_OCULTA'
  ): void {
    if (!this.estadoSubject.value.activo) return;
    this.limpiarTemporizadores();
    this.publicarEstado({ activo: false, motivo });
  }

  /**
   * Entrega cambios dentro de Angular aunque los listeners y timers se
   * ejecuten fuera de la zona para evitar ciclos de detección innecesarios.
   */
  private publicarEstado(estado: EstadoActividadUsuario): void {
    this.zone.run(() => this.estadoSubject.next(estado));
  }

  /**
   * Cancela la gracia de segundo plano cuando una nueva visibilidad o
   * programación reemplaza su callback pendiente.
   */
  private limpiarTemporizadorPestanaOculta(): void {
    clearTimeout(this.temporizadorPestanaOculta);
    this.temporizadorPestanaOculta = null;
  }

  /**
   * Cancela todos los vencimientos internos sin alterar por sí mismo el estado
   * publicado a sockets y consultas periódicas.
   */
  private limpiarTemporizadores(): void {
    clearTimeout(this.temporizadorInactividad);
    this.temporizadorInactividad = null;
    this.limpiarTemporizadorPestanaOculta();
  }
}
